import { TFunction, useTranslation } from '@mc/i18n'
import { PlansTier } from '../../types/planProducts'
import {
  getTranslationResources,
  type Benefits,
  type TransProps,
} from './getTranslationResources'

type UseTranslationParams = Parameters<typeof useTranslation>

type TBenefitFunc = (
  i18nKey: TransProps['i18nKey'],
  values: {
    tier: PlansTier
    defaultValue?: string | number | boolean
  } & Parameters<TFunction>['2'],
) => ReturnType<TFunction>

/**
 * The useBenefitTranslation hook is designed to simplify the translation of tier benefits. It utilizes
 * the @mc/i18n packages to incorporate information into a specified translation key.
 *
 * See BenefitTrans component for more details.
 */
export const useBenefitTranslation = ({
  name,
  ns,
  options,
}: {
  name: Benefits
  ns?: UseTranslationParams['0']
  options?: UseTranslationParams['1']
}) => {
  const { t: _t, ...others } = useTranslation(ns, options)

  const t: TBenefitFunc = (_i18nKey, { tier, defaultValue, ..._values }) => {
    const { i18nKey, values } = getTranslationResources({
      name,
      tier,
      defaultValue,
      i18nKey: _i18nKey,
      values: _values,
    })

    if (!i18nKey) return ''

    return _t(i18nKey, values)
  }

  return {
    ...others,
    t,
  }
}
